/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-globals */
import { action } from '@ember/object';
import { service } from '@ember/service';
import type ErrorHandlerService from 'common/services/error-handler';
import { validateAndMapErrors } from 'common/utils/validate-and-map';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type {
  ProxyWrappedChangeset,
  ValidationFunction,
} from 'ember-form-changeset-validations';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import type {
  ApplicationChangeset,
  ApplicationDTO,
} from 'frontend/changesets/application';
import type ApplicationChangesetService from 'frontend/services/changesets/apply';
import ApplicationValidation from 'frontend/validations/apply';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type { ContactFormChangeset } from 'frontend/changesets/contact-form';
import type { RecontactFormChangeset } from 'frontend/changesets/recontact-form';
import makeChangesetReactive from 'common/utils/reactive-changeset';
import type { PagesApplyBaseArgs } from './base';
import PagesApplyBase from './base';
import type FastbootService from 'ember-cli-fastboot';
import type { UserKind } from 'common/addon/enums/user-kind.enum';

interface PagesApplyArgs extends PagesApplyBaseArgs {
  changeset: ApplicationChangeset;
  contactChangeset: ContactFormChangeset;
  recontactChangeset: RecontactFormChangeset;
}

// eslint-disable-next-line ember/no-empty-glimmer-component-classes
export default class PagesApply extends PagesApplyBase<PagesApplyArgs> {
  @tracked declare changeset: ApplicationChangeset;
  @tracked declare currentSlug: string;
  @tracked isOpenedModal = false;
  @service declare flashMessages: FlashMessageService;
  @service declare intl: IntlService;
  @service declare errorHandler: ErrorHandlerService;
  @service declare fastboot: FastbootService;
  @tracked declare type: string;
  @service('changesets/apply')
  declare applicationService: ApplicationChangesetService;

  validationFunction: ValidationFunction<ApplicationDTO> = async (dto) =>
    validateAndMapErrors(ApplicationValidation, dto);

  constructor(owner: unknown, args: PagesApplyArgs) {
    super(owner, args);
    void taskFor(this.loadChangeset).perform();
  }

  @dropTask *loadChangeset() {
    this.changeset = yield this.applicationService.changesetFromAction(
      this.args.action
    );
    makeChangesetReactive(this, this.changeset, ApplicationValidation);
  }

  @action
  async saveApplicationForm(
    changeset: ProxyWrappedChangeset<ApplicationChangeset>
  ) {
    try {
      await this.applicationService.save(changeset);
      this.flashMessages.success(
        this.intl.t(
          this.isVirtualAction
            ? 'components.pages.apply.success_message_waiting'
            : 'components.pages.apply.success_message'
        )
      );
      this.addDataLayerEvent(
        changeset.data.kind!,
        changeset.data.newsAgreement
      );
      await this.router.transitionTo('catalog.action.apply.thank-you');
    } catch (e: unknown) {
      const error = (await e) as AdapterError;
      this.errorHandler.handle(changeset, error.errors);
    }
  }

  @action openModal() {
    this.isOpenedModal = !this.isOpenedModal;
  }

  private addDataLayerEvent(kind: UserKind, optin: boolean) {
    if (!this.fastboot.isFastBoot) {
      const kindMap: Record<UserKind, string> = {
        unemployed: "Demandeurs d'emploi",
        company: 'Entreprises',
        teacher: 'Enseignement',
      };
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'form_submit',
        target: kindMap[kind],
        newsletter_optin: optin,
      });
    }
  }
}
